/**
 * One transcript entry — RN counterpart of the web widget's `message-item`.
 *
 * Two shapes, decided by one flag: user messages always sit in a bubble on the
 * surface colour; agent messages render bubble-less on the panel unless the
 * theme sets `agentBubbleColor`, which may be a gradient and so paints through
 * <GradientFill>.
 *
 * The web `data-ringg-role` attribute has no counterpart here — an RN node
 * carries exactly one queryable string, `testID`, and that slot belongs to the
 * contract name. Which side spoke stays readable from the bubble's alignment.
 */

import type { FC, ReactNode } from "react";
import { Linking, StyleSheet, Text, View } from "react-native";
import type { ChatMessage } from "@ringg/core";

import { GradientFill, solidColor } from "../lib/gradient";
import { Markdown } from "../lib/markdown";
import { ringgId } from "../lib/testing";
import { resolveFontFamily, useWidgetTheme } from "../lib/widget-theme";
import { Touchable } from "./ui/pressable";

interface MessageItemProps {
  /** Position in the transcript. Web feeds it to the list key; kept so both
   *  trees take the same props and a caller can be ported line for line. */
  itemIndex: number;
  message: ChatMessage;
  /** Rendered full width below the body, inside the bubble. */
  children?: ReactNode;
  /** Feedback replay shows component prompts without their text. */
  hideMessageContent?: boolean;
}

/** `text-[15px]` on the bubble's `leading-[1.3]`. */
const BODY_FONT_SIZE = 15;
const LINE_HEIGHT_RATIO = 1.3;

/** The RAG citation's `text-[13px]`. */
const SOURCE_FONT_SIZE = 13;

/** `rounded-xl`, with the corner nearest the speaker pinched to 2. */
const BUBBLE_RADIUS = 12;
const ANCHORED_RADIUS = 2;

/** `pl-3 pr-2 py-2` — the bubble is tighter on the side the content ends. */
const BUBBLE_PADDING_LEFT = 12;
const BUBBLE_PADDING_RIGHT = 8;
const BUBBLE_PADDING_VERTICAL = 8;

/** `gap-2.5` between body, source link and attachment. */
const CONTENT_GAP = 10;

const MessageItem: FC<MessageItemProps> = ({ message, children, hideMessageContent = false }) => {
  const theme = useWidgetTheme();
  // Side is decided by isSelf, never by name — live agents carry their display
  // name ("Neha"), so name-sniffing misclassifies them as user messages.
  const isAgentMessage = !message.isSelf;
  const agentBubble = theme.agentBubbleColor.trim();
  const bubbled = !isAgentMessage || Boolean(agentBubble);
  const shouldRenderMessageBody = !hideMessageContent && Boolean(message.message.trim());
  const sourceUrl = message.sourceUrl;
  const fontFamily = resolveFontFamily(theme.fontFamily);

  const body = isAgentMessage ? (
    <View testID={ringgId("message-body")}>
      <Markdown
        content={message.message}
        color={theme.textColor}
        mutedColor={theme.mutedTextColor}
        // prose leaves markdown links at its near-black default. A tappable run
        // on a phone has to read as tappable, so it takes the same primary
        // colour the source link below already uses.
        linkColor={solidColor(theme.primaryColor)}
        fontSize={BODY_FONT_SIZE}
        fontFamily={fontFamily}
      />
    </View>
  ) : (
    // Self messages are an echo of what the user typed; running them through
    // markdown would reformat their own text back at them.
    <Text testID={ringgId("message-body")} style={[styles.body, { color: theme.textColor, fontFamily }]}>
      {message.message}
    </Text>
  );

  return (
    <View style={isAgentMessage ? styles.rowStart : styles.rowEnd} testID={ringgId("message")}>
      <View testID={ringgId("message-bubble")} style={[styles.bubble, bubbled ? [styles.bubbleBox, isAgentMessage ? styles.agentCorners : styles.userCorners] : null]}>
        {/* Painted as a layer rather than a background colour: agentBubbleColor
            may be a gradient, and RN has no gradient `backgroundColor`. */}
        {bubbled ? <GradientFill color={isAgentMessage ? agentBubble : theme.surfaceColor} style={StyleSheet.absoluteFill} /> : null}
        <View testID={ringgId("message-content")} style={[styles.content, bubbled ? styles.contentPadding : null]}>
          {shouldRenderMessageBody ? body : null}
          {sourceUrl ? (
            <Touchable
              testID={ringgId("message-source")}
              style={styles.source}
              accessibilityLabel={`Source: ${sourceUrl}`}
              onPress={() => {
                // Best-effort: a malformed or unhandled scheme must not crash
                // the widget. `Linking` is RN's `target="_blank"`.
                void Linking.openURL(sourceUrl).catch(() => {});
              }}
            >
              <Text style={[styles.sourceText, { color: solidColor(theme.primaryColor), fontFamily }]}>Source: {sourceUrl}</Text>
            </Touchable>
          ) : null}
          {children ? (
            <View testID={ringgId("message-attachment")} style={styles.attachment}>
              {children}
            </View>
          ) : null}
        </View>
      </View>
    </View>
  );
};

export default MessageItem;

const styles = StyleSheet.create({
  // Web anchors the bubble with `mr-auto` / `ml-auto` inside a `flex-col
  // items-start` column; alignItems says the same thing natively.
  rowStart: { alignItems: "flex-start" },
  rowEnd: { alignItems: "flex-end" },
  // `max-w-[80%]` — the bubble hugs its content up to 80% of the transcript.
  bubble: { maxWidth: "80%" },
  // The gradient layer fills the bubble, so the padding lives one level in on
  // the content; `overflow` is what clips that layer to the rounded corners.
  bubbleBox: { overflow: "hidden" },
  agentCorners: { borderTopLeftRadius: BUBBLE_RADIUS, borderTopRightRadius: BUBBLE_RADIUS, borderBottomLeftRadius: ANCHORED_RADIUS, borderBottomRightRadius: BUBBLE_RADIUS },
  userCorners: { borderTopLeftRadius: BUBBLE_RADIUS, borderTopRightRadius: BUBBLE_RADIUS, borderBottomLeftRadius: BUBBLE_RADIUS, borderBottomRightRadius: ANCHORED_RADIUS },
  content: { gap: CONTENT_GAP },
  contentPadding: { paddingLeft: BUBBLE_PADDING_LEFT, paddingRight: BUBBLE_PADDING_RIGHT, paddingVertical: BUBBLE_PADDING_VERTICAL },
  body: { fontSize: BODY_FONT_SIZE, lineHeight: BODY_FONT_SIZE * LINE_HEIGHT_RATIO },
  // `w-fit`: the citation's touch area stops at the end of the URL rather than
  // spanning the bubble. Press feedback replaces the web's hover underline.
  source: { alignSelf: "flex-start" },
  sourceText: { fontSize: SOURCE_FONT_SIZE, lineHeight: SOURCE_FONT_SIZE * LINE_HEIGHT_RATIO },
  attachment: { alignSelf: "stretch" },
});
